import { ComponentProps, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ChevronDownIcon, ChevronUpIcon, FlatList, HStack, Pressable } from 'native-base';
import { groupBy, map, uniq } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import { Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import {
  AutoAssignCleaningRequest,
  AutoScheduleCleaningRequest,
  BulkUpdateTaskScheduleRequest,
  CleaningScheduleTypeEnum,
  CleaningTaskStatusEnum,
  StringTranslation,
} from '@pimm/services/lib/sms-workforce/types';
import { AutoAssign, AutoSchedule, BulkUpdateTaskSchedule } from '@pimm/services/lib/sms-workforce/services';
import { formatToISOString, stringToDateLocal } from '@app/utils/date-formatter';
import { useSiteTime } from '@app/features/store-core';
import { CleaningPlan, CleaningTaskItem } from '../reducers';
import { CleaningTasksTableItem } from './cleaning-task-table-item';
import { CleaningTasksEditableView, EditModeType } from './cleaning-tasks-editable-view';
import { useCleaningAssignments } from '../context';
import { getScheduleColor } from '../utils';
import { useCleaningToast } from '../hooks';

type CleaningTasksTableProps = Pick<ComponentProps<typeof CleaningTasksTableItem>, 'onOpenLinkDocument'> & {
  isEditable?: boolean;
  plan: CleaningPlan;
  editMode?: EditModeType;
  selectedTaskIds?: Set<number>;
  onEditModeChange?: (mode: EditModeType) => void;
  onSelectionChange?: (taskIds: Set<number>) => void;
  onExitEditMode?: () => void;
  onClearFilters?: () => void;
  /** When true, passes isInsideModal to CleaningTasksEditableView so PopoverBulkScheduleMenu renders as a Modal */
  isInsideModal?: boolean;
};

type NonEditableRow =
  | { type: 'header'; id: string }
  | {
      type: 'category';
      id: string;
      category: string;
      idx: number;
      completedCount: number;
      totalCount: number;
      translations?: StringTranslation[];
    }
  | { type: 'task'; id: string; task: CleaningTaskItem }
  | { type: 'empty'; id: string };

export const CleaningTasksTable = ({
  isEditable,
  plan,
  editMode,
  selectedTaskIds,
  onEditModeChange,
  onSelectionChange,
  onExitEditMode,
  isInsideModal,
  ...props
}: CleaningTasksTableProps) => {
  const { translate } = useAppLocale();
  const { t } = useTranslation();
  const { siteId, updateCleaningTask, refetchAssignments } = useCleaningAssignments();
  const { showToast } = useCleaningToast();
  const categoryRefs = useRef<{ [key: string]: any }>({});
  const siteTime = useSiteTime();
  const [currentWeek] = useState<ReturnType<typeof siteTime.toStartEndOfWeek>>(siteTime.toStartEndOfWeek(plan.date));
  const [dopStartHour] = useState<number>(siteTime.toDayBlocks(plan.date)[0]?.startTime?.getHours() ?? 5);
  const [autoScheduleSubmitted, setAutoScheduleSubmitted] = useState(false);
  const [autoAssignSubmitted, setAutoAssignSubmitted] = useState(false);

  let categories = uniq(map(plan.tasks, _ => _.category).filter(Boolean)) as string[];
  // If daily tasks, making sure to show a default tab items if empty
  if (categories.length === 0 && plan.scheduleType === CleaningScheduleTypeEnum.Daily) {
    categories = ['Daypart 1-3', 'Daypart 4-6'];
  }

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(categories));
  const [scrollToCategory, setScrollToCategory] = useState<string | undefined>(undefined);

  const { filteredCategories, filteredTasksByCategory, originalTasksByCategory } = useMemo(() => {
    const grouped = groupBy(plan.tasks, 'category');
    const filtered = categories.reduce<Record<string, CleaningTaskItem[]>>((acc, category) => {
      const tasks = grouped[category] ?? [];
      if (tasks.length) acc[category] = tasks;
      return acc;
    }, {});
    return {
      filteredCategories: Object.keys(filtered),
      filteredTasksByCategory: filtered,
      originalTasksByCategory: grouped,
    };
  }, [categories, plan.tasks]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const autoScheduleMutation = useMutation({
    mutationFn: AutoSchedule,
    onSuccess: () => {
      setAutoScheduleSubmitted(true);
      setTimeout(() => {
        refetchAssignments();
      }, 500);
    },
  });

  const handleAutoScheduleAll = () => {
    if (!selectedTaskIds || selectedTaskIds.size === 0) return;
    const payload: AutoScheduleCleaningRequest = {
      siteId,
      businessDate: formatToISOString(plan.date),
      cleaningTaskIds: Array.from(selectedTaskIds),
      scheduleType: plan.scheduleType,
    };

    autoScheduleMutation.mutate(payload);
  };

  const autoAssignMutation = useMutation({
    mutationFn: AutoAssign,
    onSuccess: (_, variables) => {
      const count = variables.cleaningTaskIds.length;
      showToast(
        'success',
        <Text size="md" color="white" fontWeight={600}>
          {count} / {count} {t('cleaning_task:tasks_selected').replace('selected', 'assigned')}
        </Text>,
      );
      setAutoAssignSubmitted(true);
      setTimeout(() => {
        refetchAssignments();
      }, 500);
    },
    onError: () => {
      showToast(
        'error',
        <Text size="md" color="white" fontWeight={600}>
          {t('cleaning_task:auto_assign_failed')}
        </Text>,
      );
    },
  });

  const handleAutoAssignAll = () => {
    if (!selectedTaskIds || selectedTaskIds.size === 0) return;
    const payload: AutoAssignCleaningRequest = {
      siteId,
      businessDate: formatToISOString(plan.date),
      cleaningTaskIds: Array.from(selectedTaskIds),
      scheduleType: plan.scheduleType,
    };
    autoAssignMutation.mutate(payload);
  };

  const handleSelectionChange = (taskIds: Set<number>) => {
    if (autoAssignSubmitted) setAutoAssignSubmitted(false);
    onSelectionChange?.(taskIds);
  };

  const handleExitEditMode = () => {
    if (autoScheduleSubmitted) {
      refetchAssignments();
    }
    setAutoScheduleSubmitted(false);
    setAutoAssignSubmitted(false);
    onExitEditMode?.();
  };

  const bulkUpdateTaskSchedule = useMutation({
    mutationFn: BulkUpdateTaskSchedule,
    onSuccess: (_, cleaning) => {
      const taskIds = cleaning.cleaningTaskIds ?? [];
      const newDueDate = stringToDateLocal(cleaning.dueDate);

      taskIds.forEach(taskId => {
        const task = plan.tasks.find(_task => _task.id === taskId);
        if (task) {
          updateCleaningTask(plan.scheduleType, {
            id: taskId,
            assignee: {
              ...task.assignee,
              dueDate: newDueDate,
              isAutoScheduled: false,
            },
          });
        }
      });
    },
  });

  const handleApplySchedule = (dateTime: Date, taskIds?: number[]) => {
    const payload: BulkUpdateTaskScheduleRequest = {
      siteId: siteId,
      businessDate: formatToISOString(plan.date),
      cleaningTaskIds: taskIds ?? [],
      dueDate: formatToISOString(dateTime),
      scheduleType: plan.scheduleType,
    };

    bulkUpdateTaskSchedule.mutate(payload);
  };

  const nonEditableRows = useMemo<NonEditableRow[]>(() => {
    const rows: NonEditableRow[] = [{ type: 'header', id: 'column-header' }];

    filteredCategories.forEach((category, idx) => {
      const tasks = filteredTasksByCategory[category] ?? [];
      const originalTasks = originalTasksByCategory[category] ?? [];
      const nonNATasks = originalTasks.filter(task => task.assignee?.status !== CleaningTaskStatusEnum.NotApplicable);
      const completedCount = nonNATasks.filter(task => task.assignee?.status === CleaningTaskStatusEnum.Complete).length;

      rows.push({
        type: 'category',
        id: `category-${category}-${idx}`,
        category,
        idx,
        completedCount,
        totalCount: nonNATasks.length,
        translations: tasks[0]?.categoryTranslations,
      });

      if (expandedCategories.has(category)) {
        if (tasks.length === 0) {
          rows.push({ type: 'empty', id: `empty-${category}-${idx}` });
        } else {
          tasks.forEach(task => {
            rows.push({ type: 'task', id: `task-${task.id}`, task });
          });
        }
      }
    });

    return rows;
  }, [expandedCategories, filteredCategories, filteredTasksByCategory, originalTasksByCategory]);

  useEffect(() => {
    if (scrollToCategory) {
      const timer = setTimeout(() => setScrollToCategory(undefined), 500);
      return () => clearTimeout(timer);
    }
  }, [scrollToCategory]);

  useEffect(() => {
    if (!isEditable) return;

    const handleScrollToCategory = (event: any) => {
      const { category } = event.detail;
      setExpandedCategories(prev => new Set(prev).add(category));
      setScrollToCategory(category);
    };

    window.addEventListener('scrollToCategory', handleScrollToCategory);
    return () => {
      window.removeEventListener('scrollToCategory', handleScrollToCategory);
    };
  }, [isEditable]);

  return (
    <Box h="full" bg={isEditable ? 'gray.50' : 'transparent'}>
      {/* ── Editable View ── */}
      {isEditable && (
        <CleaningTasksEditableView
          plan={plan}
          categories={categories}
          filteredCategories={filteredCategories}
          filteredTasksByCategory={filteredTasksByCategory}
          originalTasksByCategory={originalTasksByCategory}
          expandedCategories={expandedCategories}
          hasActiveFilters={false}
          categoryRefs={categoryRefs}
          scrollToCategory={scrollToCategory}
          onToggleCategory={toggleCategory}
          editMode={editMode}
          selectedTaskIds={selectedTaskIds}
          onSelectionChange={handleSelectionChange}
          onExitEditMode={handleExitEditMode}
          onAutoAssignAll={handleAutoAssignAll}
          isAutoAssignLoading={autoAssignMutation.isLoading}
          isAutoAssignSubmitted={autoAssignSubmitted}
          onAutoScheduleAll={handleAutoScheduleAll}
          isAutoScheduleSubmitted={autoScheduleSubmitted}
          isAutoScheduleLoading={autoScheduleMutation.isLoading}
          scheduledDate={plan.date}
          isScheduleTimeLoading={bulkUpdateTaskSchedule.isLoading}
          isScheduleTimeDisabled={plan.scheduleType === CleaningScheduleTypeEnum.Daily}
          dopStartHour={plan.scheduleType === CleaningScheduleTypeEnum.Daily ? dopStartHour : undefined}
          scheduleMin={
            plan.scheduleType === CleaningScheduleTypeEnum.Monthly
              ? new Date(plan.date.getFullYear(), plan.date.getMonth(), 1)
              : currentWeek.startDate
          }
          scheduleMax={
            plan.scheduleType === CleaningScheduleTypeEnum.Monthly
              ? new Date(plan.date.getFullYear(), plan.date.getMonth() + 1, 0)
              : currentWeek.endDate
          }
          onApplyBulkScheduleTime={handleApplySchedule}
          onOpenLinkDocument={props.onOpenLinkDocument}
          onClearFilters={props.onClearFilters}
          isInsideModal={isInsideModal}
        />
      )}
      {/* ── Non-editable: single box with ColHeaders + sticky section headers ── */}
      {!isEditable && (
        <Box flex={1}>
          {filteredCategories.length > 0 && (
            <FlatList
              flex={1}
              data={nonEditableRows}
              keyExtractor={item => item.id}
              stickyHeaderIndices={[0]}
              showsVerticalScrollIndicator
              renderItem={({ item }) => {
                if (item.type === 'header') {
                  return (
                    <HStack h={9} bg="gray.50" borderBottomWidth={1}>
                      <Box w="50px" h="full" />
                      <Box justifyContent="center" px={2} flex={1} h="full">
                        <Text size="sm" color="gray.500" fontWeight={600}>
                          {t('cleaning_task:task')}
                        </Text>
                      </Box>
                      <Box
                        display={{ base: 'none', md: 'flex' }}
                        alignItems="flex-start"
                        justifyContent="center"
                        px={2}
                        w="16%"
                        maxWidth="180px"
                        h="full"
                      >
                        <Text size="sm" color="gray.500" fontWeight={600} textAlign="left">
                          {t('cleaning_task:due_by')}
                        </Text>
                      </Box>
                      <Box display={{ base: 'none', md: 'flex' }} justifyContent="center" px={2} w="22%" maxWidth="220px" h="full">
                        <Text size="sm" color="gray.500" fontWeight={600}>
                          {t('cleaning_task:assigned_to')}
                        </Text>
                      </Box>
                      <Box justifyContent="center" w="70px" h="full">
                        <Text size="sm" color="gray.500" fontWeight={600} textAlign="center">
                          {t('cleaning_task:n_a')}
                        </Text>
                      </Box>
                    </HStack>
                  );
                }

                if (item.type === 'category') {
                  const isExpanded = expandedCategories.has(item.category);
                  const scheduleColorName = getScheduleColor(plan.scheduleType);
                  const allDone = item.totalCount > 0 && item.completedCount === item.totalCount;
                  const partial = item.completedCount > 0 && item.completedCount < item.totalCount;

                  return (
                    <Box
                      ref={ref => {
                        if (ref) categoryRefs.current[item.category] = ref;
                      }}
                    >
                      <Pressable onPress={() => toggleCategory(item.category)}>
                        <HStack
                          alignItems="center"
                          space={2}
                          px={4}
                          py={2.5}
                          bg="gray.25"
                          borderTopWidth={item.idx > 0 ? 1 : 0}
                          borderBottomWidth={1}
                          borderColor="gray.200"
                        >
                          <Text size="lg" fontWeight={700} color="gray.900" flex={1}>
                            {translate(item.category, item.translations)}
                          </Text>
                          <Box
                            px={2}
                            py={0.5}
                            rounded="full"
                            borderWidth={allDone ? 0 : 1}
                            borderColor={partial ? `${scheduleColorName}.200` : 'gray.200'}
                            bg={allDone ? `${scheduleColorName}.500` : partial ? `${scheduleColorName}.50` : 'gray.100'}
                          >
                            <Text
                              size="sm"
                              lineHeight="sm"
                              fontWeight={600}
                              color={allDone ? 'white' : partial ? `${scheduleColorName}.700` : 'gray.500'}
                              letterSpacing={1}
                            >
                              {item.completedCount}/{item.totalCount}
                            </Text>
                          </Box>
                          <Box>
                            {isExpanded ? <ChevronUpIcon size={4} color="gray.500" /> : <ChevronDownIcon size={4} color="gray.500" />}
                          </Box>
                        </HStack>
                      </Pressable>
                    </Box>
                  );
                }

                if (item.type === 'empty') {
                  return (
                    <Box alignItems="center" justifyContent="center" minH="60px">
                      <Text size="md" color="gray.500" fontWeight={500}>
                        {t('cleaning_task:no_available_task')}
                      </Text>
                    </Box>
                  );
                }

                return (
                  <CleaningTasksTableItem
                    isEditable={false}
                    scheduleDate={plan.date}
                    scheduleType={plan.scheduleType}
                    task={item.task}
                    dopStartHour={plan.scheduleType === CleaningScheduleTypeEnum.Daily ? dopStartHour : undefined}
                    onOpenLinkDocument={props.onOpenLinkDocument}
                  />
                );
              }}
            />
          )}

          {filteredCategories.length === 0 && (
            <Box alignItems="center" justifyContent="center" minH="60px">
              <Text size="md" color="gray.500" fontWeight={500}>
                {t('cleaning_task:no_available_task')}
              </Text>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
